import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Navigate } from 'react-router-dom';
import { register } from '../redux/actions/auth';
import { useFormik } from 'formik';
import * as yup from 'yup';

import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import { Alert, AlertTitle, Stack } from '@mui/material';
import { CircularProgress } from '@mui/material';

import { createTheme, ThemeProvider } from '@mui/material/styles';
import Copyright from '../components/Copyright';
import ProjectTitle from '../components/ProjectTitle';

import BackgroundImage from '../assets/img/antalya.png';

import { agrementURL } from '../api';

const validationsForm = yup.object({
  name: yup.string().required('İsim bilgisini giriniz.'),
  surname: yup.string().required('Soyisim bilgisini giriniz.'),
  email: yup
    .string()
    .email('Geçerli bir mail adresi giriniz.')
    .required('Email adresinizi giriniz.'),
  phoneNumber: yup
    .string()
    .matches(/^\d{10}$/, '10 haneli bir telefon numarası giriniz')
    .required('Telefon numaranızı giriniz.'),
  password: yup
    .string()
    .min(8, 'Parola en az 8 karakter içermelidir.')
    .required('Parolanızı giriniz.'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'Parolalar eşleşmiyor.')
    .required('Parolanızı doğrulayın.'),
  company: yup.string().required('Çalışılan kurum bilgisini giriniz.'),
  job: yup.string().required('Meslek bilgisini giriniz.'),
  license: yup.string().required('Lisans anahtarını giriniz.'),
});

const theme = createTheme();

const SignUpPage = (props) => {
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);

  const { isLoggedIn } = useSelector((state) => state.auth);
  const { message } = useSelector((state) => state.authMessage);

  const [agrementCheck, setAgrementCheck] = React.useState(false);

  const handleChangeAgrement = (event) => {
    let status = event.target.checked;
    setAgrementCheck(status);
  };

  const formik = useFormik({
    initialValues: {
      name: '',
      surname: '',
      email: '',
      phoneNumber: '',
      password: '',
      confirmPassword: '',
      company: '',
      job: '',
      license: '',
    },
    validationSchema: validationsForm,
    onSubmit: (values) => {
      const {
        name,
        surname,
        email,
        phoneNumber,
        password,
        company,
        job,
        license,
      } = values;
      setLoading(true);
      dispatch(
        register(
          name,
          surname,
          email,
          phoneNumber,
          password,
          company,
          job,
          license
        )
      )
        .then(() => {
          props.history.push('/');
          window.location.reload();
        })
        .catch(() => {
          setLoading(false);
        });
    },
  });

  if (isLoggedIn) {
    return <Navigate to="/map" />;
  }

  return (
    <ThemeProvider theme={theme}>
      <div
        style={{
          backgroundImage: `url(${BackgroundImage})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          minHeight: '100vh',
          border: '0.1px solid black',
        }}
      >
        <Container maxWidth="md">
          <Grid
            container
            component="main"
            sx={{
              verticalAlign: 'center',
              marginTop: '5%',
              marginBottom: '5%',
              alignContent: 'center',
              alignItems: 'center',
              justifyContent: 'center',
              alignSelf: 'center',
            }}
          >
            <CssBaseline />
            <Grid item component={Paper} elevation={6} square>
              <Box
                sx={{
                  my: 8,
                  mx: 4,
                  marginTop: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <ProjectTitle />
                <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
                  <AccountCircleIcon fontSize="large" />
                </Avatar>
                <Typography component="h1" variant="h5">
                  Kayıt Ol
                </Typography>
                {loading && (
                  <CircularProgress
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      mt: 2,
                      mb: 2,
                    }}
                  />
                )}
                <Box
                  component="form"
                  noValidate
                  onSubmit={formik.handleSubmit}
                  sx={{ mt: 3 }}
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        autoComplete="given-name"
                        name="name"
                        required
                        fullWidth
                        id="name"
                        label="Ad"
                        autoFocus
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.name && Boolean(formik.errors.name)
                        }
                        helperText={formik.touched.name && formik.errors.name}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        required
                        fullWidth
                        id="surname"
                        label="Soyad"
                        name="surname"
                        autoComplete="family-name"
                        value={formik.values.surname}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.surname &&
                          Boolean(formik.errors.surname)
                        }
                        helperText={
                          formik.touched.surname && formik.errors.surname
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        size="small"
                        required
                        fullWidth
                        id="email"
                        label="Email Adresi"
                        name="email"
                        autoComplete="email"
                        value={formik.values.email}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.email && Boolean(formik.errors.email)
                        }
                        helperText={formik.touched.email && formik.errors.email}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        required
                        fullWidth
                        name="password"
                        label="Parola"
                        type="password"
                        id="password"
                        autoComplete="new-password"
                        value={formik.values.password}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.password &&
                          Boolean(formik.errors.password)
                        }
                        helperText={
                          formik.touched.password && formik.errors.password
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        size="small"
                        required
                        fullWidth
                        name="confirmPassword"
                        label="Parolayı Doğrulayın"
                        type="password"
                        id="confirmPassword"
                        autoComplete="new-password"
                        value={formik.values.confirmPassword}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.confirmPassword &&
                          Boolean(formik.errors.confirmPassword)
                        }
                        helperText={
                          formik.touched.confirmPassword &&
                          formik.errors.confirmPassword
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        size="small"
                        required
                        fullWidth
                        id="phoneNumber"
                        label="Telefon Numarası (Başında 0 olmadan 10 haneli)"
                        name="phoneNumber"
                        autoComplete="phoneNumber"
                        value={formik.values.phoneNumber}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.phoneNumber &&
                          Boolean(formik.errors.phoneNumber)
                        }
                        helperText={
                          formik.touched.phoneNumber &&
                          formik.errors.phoneNumber
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        size="small"
                        name="company"
                        required
                        fullWidth
                        id="company"
                        label="Çalışılan Kurum"
                        value={formik.values.company}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.company &&
                          Boolean(formik.errors.company)
                        }
                        helperText={
                          formik.touched.company && formik.errors.company
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        size="small"
                        name="job"
                        required
                        fullWidth
                        id="job"
                        label="Meslek"
                        value={formik.values.job}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.job && Boolean(formik.errors.job)}
                        helperText={formik.touched.job && formik.errors.job}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        sx={{ backgroundColor: 'rgba(0, 128, 0, 0.2)' }}
                        size="small"
                        name="license"
                        required
                        fullWidth
                        id="license"
                        label="Lisans Anahtarı"
                        value={formik.values.license}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={
                          formik.touched.license &&
                          Boolean(formik.errors.license)
                        }
                        helperText={
                          formik.touched.license && formik.errors.license
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Stack direction={'row'}>
                        <Checkbox
                          onChange={handleChangeAgrement}
                          color="primary"
                        />
                        <Typography>
                          İTÜ – UHUZAM’ın sağladığı görüntü verisi ya da katma
                          değerli ürünleri{' '}
                          <Link href={agrementURL}>Lisans Sözleşmesi</Link>
                          ’nde belirtilen koşullarda kullanacağımı taahhüt
                          ederim.
                        </Typography>
                      </Stack>
                    </Grid>
                  </Grid>
                  <Button
                    disabled={!agrementCheck}
                    type="submit"
                    fullWidth
                    variant="contained"
                    sx={{ mt: 3, mb: 2 }}
                  >
                    Kayıt Ol
                  </Button>
                  {message && (
                    <Alert severity={message.type}>
                      <AlertTitle>Bilgi</AlertTitle>
                      <strong>{message.message}</strong>
                    </Alert>
                  )}
                  <Grid container justifyContent="flex-end">
                    <Grid item>
                      <Link href="/signin" variant="body2">
                        Hesabınız var mı? Oturum açın
                      </Link>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
              <Copyright sx={{ mt: 5 }} />
              <br />
            </Grid>
          </Grid>
        </Container>
      </div>
    </ThemeProvider>
  );
};

export default SignUpPage;
